import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { config } from "../lib/config";
import { timeZoneOptions, currentTimeIn } from "../shared/timezones";

// Commissioner dashboard. Operationally powerful, competitively BLIND: sees
// inventory COUNTS and notification readiness, never another member's question
// content, transcripts, or future selections. No exact future drop time is shown.
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PLAY_WINDOWS = [1, 2, 4, 8, 12, 24, 48]; // hours a drop stays open to play
const ANSWER_TIMERS = [10, 15, 20, 30]; // seconds per question

function DayPicker({ selected, onToggle }: { selected: number[]; onToggle: (d: number) => void }) {
  return (
    <div className="row" style={{ flexWrap: "wrap" }}>
      {WEEKDAYS.map((w, i) => {
        const sel = selected.includes(i);
        return (
          <button
            key={w}
            onClick={() => onToggle(i)}
            style={{
              minWidth: 46,
              padding: "10px 12px",
              borderRadius: 10,
              fontWeight: 800,
              cursor: "pointer",
              border: sel ? "2px solid var(--accent)" : "1px solid #3a4050",
              background: sel ? "var(--accent)" : "var(--surface-2)",
              color: sel ? "#1a1400" : "var(--text)",
            }}
          >
            {w}
          </button>
        );
      })}
    </div>
  );
}

export function Commissioner() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<any>(null);
  const [league, setLeague] = useState<any>(null);
  const [invite, setInvite] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleMsg, setRescheduleMsg] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Season config draft.
  const [startsOn, setStartsOn] = useState(new Date().toISOString().slice(0, 10));
  const [endsOn, setEndsOn] = useState(new Date(Date.now() + 13 * 86400000).toISOString().slice(0, 10));
  const [days, setDays] = useState<number[]>([1, 3, 5]);
  const [winStart, setWinStart] = useState("09:00");
  const [winEnd, setWinEnd] = useState("21:00");
  const [playWindow, setPlayWindow] = useState(12);
  const [answerSeconds, setAnswerSeconds] = useState(20);
  const [perDrop, setPerDrop] = useState(2);
  const [cadence, setCadence] = useState("once");
  const [wildcardDays, setWildcardDays] = useState<number[]>([]);
  const [wildcardTheme, setWildcardTheme] = useState("random");

  // Editable league basics (no money/stakes).
  const [lname, setLname] = useState("");
  const [ltz, setLtz] = useState("");

  async function load() {
    setLoadErr("");
    const { data: lg } = await supabase.from("leagues").select("*").eq("id", leagueId).single();
    setLeague(lg);
    if (lg) { setLname(lg.name ?? ""); setLtz(lg.timezone ?? ""); }
    const s = await api(`/api/league/admin`, { body: { op: "status", leagueId } });
    setStatus(s);
    if ((s as any).season) {
      const se = (s as any).season;
      setStartsOn(se.starts_on);
      setEndsOn(se.ends_on);
      setDays(se.active_weekdays ?? []);
      setWinStart(se.drop_window_start_local);
      setWinEnd(se.drop_window_end_local);
      setPlayWindow(se.drop_open_hours ?? 12);
      setAnswerSeconds(se.question_seconds ?? 20);
      setPerDrop(se.default_questions_per_drop ?? 2);
      setCadence(se.check_in_cadence ?? "once");
      setWildcardDays(se.wildcard_weekdays ?? []);
      setWildcardTheme(se.wildcard_theme ?? "random");
    }
  }
  useEffect(() => { load().catch((e: any) => setLoadErr(e?.message ?? "Could not load the commissioner page.")); }, [leagueId]);

  const toggle = (setter: React.Dispatch<React.SetStateAction<number[]>>) => (d: number) =>
    setter((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]).sort());

  // Every commissioner action goes through here. Three of them used to call the
  // API with no try/catch and no busy state, so a failure rejected an un-awaited
  // promise and the button simply did nothing. This is the same treatment `op`
  // already had, applied to the rest of them.
  async function run(working: string, fn: () => Promise<string>) {
    setBusy(true);
    setActionMsg(working);
    try {
      const msg = await fn();
      await load();
      setActionMsg(msg);
    } catch (e: any) {
      setActionMsg(e?.message ?? "That didn't work. Try it again.");
    } finally {
      setBusy(false);
    }
  }

  async function saveLeague() {
    await run("Saving...", async () => {
      await api(`/api/league/admin`, { body: { op: "update", leagueId, name: lname, timezone: ltz } });
      return "League settings saved.";
    });
  }

  async function saveConfig() {
    if (days.length < 1) { setActionMsg("Pick at least one drop day."); return; }
    if (endsOn < startsOn) { setActionMsg("The end date has to be on or after the start date."); return; }
    if (winEnd <= winStart) { setActionMsg("The drop time range has to end after it starts."); return; }
    await run("Saving...", async () => {
      const r = await api<{ note?: string }>(`/api/league/admin`, {
        body: {
          op: "configure", leagueId,
          startsOn, endsOn, activeWeekdays: days,
          windowStart: winStart, windowEnd: winEnd,
          playWindowHours: playWindow, answerSeconds, questionsPerDrop: perDrop,
          checkInCadence: cadence, wildcardWeekdays: wildcardDays, wildcardTheme,
        },
      });
      return r?.note ?? "Settings saved.";
    });
  }

  async function activate() {
    await run("Setting the schedule...", async () => {
      const r = await api<{ note?: string }>(`/api/league/admin`, { body: { op: "activate", leagueId } });
      return r?.note ?? "Season is live.";
    });
  }

  // Rebuilds only the drops that haven't opened yet. Already-played drops are
  // untouched, but the upcoming secret times all change — so confirm first.
  async function reschedule() {
    const ok = window.confirm(
      "Rebuild the remaining schedule from your current settings?\n\n" +
      "Drops that already happened stay exactly as they are. Every upcoming drop gets a new secret time."
    );
    if (!ok) return;
    setRescheduling(true);
    setRescheduleMsg("");
    try {
      // The server builds this message: it is the only side that knows how many
      // times landed in the past and which day the next drop falls on.
      const r = await api<{ upcoming: number; kept: number; note?: string }>(`/api/league/admin`, { body: { op: "reschedule", leagueId } });
      setRescheduleMsg(r.note ?? `Done. ${r.upcoming} drop${r.upcoming === 1 ? "" : "s"} scheduled ahead.`);
      await load();
    } catch (e: any) {
      setRescheduleMsg(e?.message ?? "Could not rebuild the schedule.");
    } finally {
      setRescheduling(false);
    }
  }
  // Binding the invite to an email up front is what makes joining browser-proof:
  // the server can then match the invite to whoever signs in with that address,
  // with no token to carry between Safari and the installed app.
  async function makeInvite() {
    const who = inviteEmail.trim().toLowerCase();
    await run("Adding...", async () => {
      const r = await api<{ inviteUrl: string }>(`/api/league/admin`, {
        body: { op: "invite", leagueId, email: who },
      });
      setInvite(r.inviteUrl);
      setInviteEmail("");
      return `${who} is on the list. Now send them the app.`;
    });
  }
  // Every commissioner action used to swallow its result. A failed call rejected
  // an un-awaited promise and the screen simply did nothing, which is why a
  // "send ping" with no live drop looked like a dead button.
  async function op(name: string, extra: any = {}, successMsg?: string) {
    await run("Working...", async () => {
      const r = await api<any>(`/api/league/admin`, { body: { op: name, leagueId, ...extra } });
      // The server note carries the delivery detail; a canned message would hide it.
      return r?.note ?? successMsg ?? "Done.";
    });
  }

  if (loadErr) {
    return (
      <div className="app">
        <h1>Commissioner</h1>
        <p className="error">{loadErr}</p>
        <button className="btn primary" onClick={() => load().catch((e: any) => setLoadErr(e?.message ?? "Still failing."))}>
          Try again
        </button>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
      </div>
    );
  }
  if (!status || !league) return <div className="app"><div className="loading">Loading…</div></div>;
  const season = status.season;
  // Removed members keep their rows for history but must not skew the counts the
  // commissioner acts on, or a removed player reads as someone still owing an update.
  const activeMembers = (status.members ?? []).filter((m: any) => m.inviteStatus !== "removed");
  const appHost = (config.appUrl || window.location.origin).replace(/^https?:\/\//, "").replace(/\/$/, "");
  // Anyone besides the commissioner, either joined or still to sign in.
  const peopleAdded =
    activeMembers.filter((m: any) => m.role !== "commissioner").length > 0 ||
    (status.pendingInvites ?? []).length > 0;
  const lowInventory = activeMembers.filter((m: any) => m.readyCount === 0);
  const totalReady = activeMembers.reduce((sum: number, m: any) => sum + (m.readyCount ?? 0), 0);

  return (
    <div className="app">
      <div className="header">
        <h1>{league.name}</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>

      {actionMsg && (
        <div className="banner">
          <span>{actionMsg}</span>
          <button className="link" onClick={() => setActionMsg("")}>dismiss</button>
        </div>
      )}

      {/* A brand new commissioner used to land on a wall of settings with nothing
          telling them what to do first, in what order, or when they were done.
          This disappears once the season is running. */}
      {season?.status !== "active" && season?.status !== "complete" && (
        <div className="card live">
          <span className="tag">Getting your league going</span>
          <ul className="checklist">
            <li className={season ? "done" : "todo"}>
              <span className="mark">{season ? "✓" : "○"}</span>
              <span>Set your season: dates, drop days, times</span>
            </li>
            <li className={peopleAdded ? "done" : "todo"}>
              <span className="mark">{peopleAdded ? "✓" : "○"}</span>
              <span>Add your people and text them the app</span>
            </li>
            <li className={totalReady > 0 ? "done" : "todo"}>
              <span className="mark">{totalReady > 0 ? "✓" : "○"}</span>
              <span>Everybody checks in, so there are questions to play</span>
            </li>
            <li className="todo">
              <span className="mark">○</span>
              <span>Start the season</span>
            </li>
          </ul>
          <p className="muted">
            Nothing drops until you start the season. A drop with no approved questions gets skipped, so
            get a few check ins in first.
          </p>
        </div>
      )}

      {/* League basics */}
      <div className="card">
        <span className="tag">League settings</span>
        <label className="muted">League name</label>
        <input className="input" value={lname} onChange={(e) => setLname(e.target.value)} />
        <label className="muted">Timezone <span style={{ opacity: 0.7 }}>(drop times follow this)</span></label>
        <select className="input" value={ltz} onChange={(e) => setLtz(e.target.value)}>
          {timeZoneOptions(ltz).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
        </select>
        {currentTimeIn(ltz) && <p className="muted">Right now it is {currentTimeIn(ltz)} there.</p>}
        <button className="btn ghost" disabled={busy} onClick={saveLeague}>Save league settings</button>
      </div>

      {/* Season setup */}
      <div className="card">
        <span className="tag">Season length</span>
        <label className="muted">Start date</label>
        <input className="input" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        <label className="muted">End date</label>
        <input className="input" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />

        <label className="muted">Check-in cadence <span style={{ opacity: 0.7 }}>(how often members are prompted for new life updates)</span></label>
        <select className="input" value={cadence} onChange={(e) => setCadence(e.target.value)}>
          <option value="once">Once at the beginning</option>
          <option value="weekly">Weekly</option>
          <option value="twice_weekly">Twice per week</option>
        </select>

        <label className="muted">Drop days <span style={{ opacity: 0.7 }}>(which days a drop can occur, pick any)</span></label>
        <DayPicker selected={days} onToggle={toggle(setDays)} />

        <label className="muted">Drop time range <span style={{ opacity: 0.7 }}>(a drop arrives at a random time in this window)</span></label>
        <div className="row">
          <input className="input" type="time" value={winStart} onChange={(e) => setWinStart(e.target.value)} />
          <input className="input" type="time" value={winEnd} onChange={(e) => setWinEnd(e.target.value)} />
        </div>
        <p className="muted">
          The drop lands at one random minute inside this range, not at the start of it. Saving a change
          rolls a new time for every remaining day, and any time that has already passed today is skipped,
          so changing this <b>during or after</b> today&apos;s range usually means no drop today.
        </p>

        <label className="muted">Play window <span style={{ opacity: 0.7 }}>(how long members have to open a drop once it hits)</span></label>
        {/* One control, not two. A chip row and a number box for the same value
            left people unsure which one counted. A season already set to some
            other number keeps its own chip so nothing gets silently changed. */}
        <div className="row" style={{ flexWrap: "wrap" }}>
          {(PLAY_WINDOWS.includes(playWindow) ? PLAY_WINDOWS : [...PLAY_WINDOWS, playWindow].sort((a, b) => a - b)).map((h) => (
            <button
              key={h}
              className={`chip ${playWindow === h ? "on" : ""}`}
              onClick={() => setPlayWindow(h)}
            >
              {h}h
            </button>
          ))}
        </div>
        <p className="muted">Miss the window and you miss the drop, so give people enough room.</p>

        <label className="muted">Answer timer <span style={{ opacity: 0.7 }}>(seconds to answer EACH question after it appears)</span></label>
        <select className="input" value={answerSeconds} onChange={(e) => setAnswerSeconds(Number(e.target.value))}>
          {ANSWER_TIMERS.map((s) => <option key={s} value={s}>{s} seconds</option>)}
        </select>

        <label className="muted">Questions per drop <span style={{ opacity: 0.7 }}>(2 or 3 keeps a drop under a minute)</span></label>
        <input className="input" type="number" min={1} max={10} value={perDrop} onChange={(e) => setPerDrop(Number(e.target.value))} />
        <p className="muted">
          A drop runs with whatever is approved and available, so asking for more than the league has ready
          just means a shorter drop.
        </p>

        <div className="row">
          <button className="btn ghost" disabled={busy} onClick={saveConfig}>{busy ? "Saving…" : "Save config"}</button>
          {season?.status === "draft" && <button className="btn primary" disabled={busy} onClick={activate}>Activate season</button>}
        </div>
        {season && <p className="muted">Status: {season.status}</p>}

        {season && season.status !== "draft" && (
          <>
            <p className="muted">
              Change anything above and just hit <b>Save config</b>. If you changed the dates, the drop
              days or the time range, the upcoming drop times are rebuilt for you automatically.
            </p>
            <button className="btn ghost" disabled={rescheduling} onClick={reschedule}>
              {rescheduling ? "Shuffling…" : "Shuffle upcoming drop times"}
            </button>
            <p className="muted">
              Only needed if you want new random times without changing any settings. Drops that already
              happened are never touched.
            </p>
            {rescheduleMsg && <p className="muted">{rescheduleMsg}</p>}
          </>
        )}
      </div>

      {/* Wildcard / trivia */}
      <div className="card">
        <span className="tag">Wildcard days</span>
        <p className="muted">On a Wildcard day, the drop is general trivia instead of questions about your people. Pick none to keep every drop about the league.</p>
        <DayPicker selected={wildcardDays} onToggle={toggle(setWildcardDays)} />
        <label className="muted">Trivia theme (type "random" or a theme like "2006", "Southern hip-hop", "NBA")</label>
        <input className="input" value={wildcardTheme} onChange={(e) => setWildcardTheme(e.target.value)} />
        <button className="btn ghost" disabled={busy} onClick={saveConfig}>Save these Wildcard days</button>
      </div>

      {/* Invites — email-bound so joining never depends on which browser opens what */}
      <div className="card">
        <span className="tag">Invite members</span>
        <p className="muted">
          <b>Nothing is emailed to them.</b> You add the address here, then you send them the app yourself
          by text. When they sign in with that exact address, they join automatically.
        </p>
        <input
          className="input"
          type="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="name@email.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
        />
        <button className="btn accent" disabled={busy || !inviteEmail.includes("@")} onClick={makeInvite}>
          Add them to the league
        </button>
        {invite && (
          <>
            <p>Added. Now text them. Either of these works:</p>
            <p className="muted">
              <b>Simplest:</b> send them <b>{appHost}</b> and tell them to sign in with the address you
              just entered.
            </p>
            <p className="muted"><b>Or</b> send them this personal link:</p>
            <input className="input" readOnly value={invite} onFocus={(e) => e.target.select()} />
            <button
              className="btn ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(invite);
                  setActionMsg("Link copied. Paste it into a text.");
                } catch {
                  setActionMsg("Could not copy. Tap the box above and copy it by hand.");
                }
              }}
            >
              Copy link
            </button>
          </>
        )}
      </div>

      {/* Members */}
      <div className="card">
        <span className="tag">Notifications</span>
        <p className="muted">
          Send yourself a test to confirm pings actually reach your phone. Close the app first, or your
          phone may hold it silently while you are looking at the screen.
        </p>
        <button className="btn ghost" disabled={busy} onClick={() => op("test-push")}>
          Send me a test notification
        </button>
        <button className="btn ghost" disabled={busy} onClick={() => op("ping-all")}>
          Ping everyone about the live drop
        </button>
      </div>

      <div className="card">
        <span className="tag">Members</span>
        {status.members.filter((m: any) => m.inviteStatus !== "removed").map((m: any) => (
          <div className="member-row" key={m.userId}>
            <div className="lb-row" style={{ borderBottom: "none", paddingBottom: 0 }}>
              <span className="lb-name">{m.name}{m.role === "commissioner" ? " ⭐" : ""}
                <br /><span className="muted" style={{ fontSize: 12 }}>{m.email}</span>
                <br /><span className="muted" style={{ fontSize: 13 }}>
                  {m.checkedIn ? "checked in ✓" : "no check-in yet"}
                  {" · "}
                  {m.devices === 0
                    ? <span style={{ color: "var(--danger)" }}>no device registered</span>
                    : `${m.devices} device${m.devices === 1 ? "" : "s"}`}
                </span>
              </span>
              {/* Reports registered devices, which is what delivery now actually
                  uses. The old badge echoed push_status and had an "Email
                  fallback" state, which promised a channel that does not exist:
                  EMAIL_PROVIDER is mock, so those members were being told they
                  were covered while receiving nothing at all. */}
              <span className={`badge ${m.devices > 0 ? "ready" : "needs"}`}>
                {m.devices > 0 ? "Gets pinged" : "Gets nothing yet"}
              </span>
              <span className="lb-points">{m.readyCount}</span>
            </div>
            <div className="row" style={{ gap: 14, paddingBottom: 12 }}>
              <button className="link" onClick={() => op("resend", { targetUserId: m.userId })}>send ping</button>
              <button className="link" onClick={() => op("test-push", { targetUserId: m.userId })}>test</button>
              <button
                className="link"
                onClick={() => {
                  if (!window.confirm(`Reset notifications for ${m.name}? He will be asked to set them up again on whatever device he opens next.`)) return;
                  op("reset-push", { targetUserId: m.userId });
                }}
              >
                reset notifications
              </button>
              {m.role !== "commissioner" && (
                <button
                  className="link"
                  onClick={() => {
                    if (!window.confirm(`Remove ${m.name} from the league? His questions stop being used and he loses access. Past results stay.`)) return;
                    op("remove-member", { targetUserId: m.userId });
                  }}
                >
                  remove
                </button>
              )}
            </div>
          </div>
        ))}
        <p className="muted">
          Numbers are approved-question counts. You cannot see anyone&apos;s question content.
          &quot;Send ping&quot; only works while a drop is live.
        </p>
        <p className="muted">
          <b>Gets nothing yet</b> means no phone is registered for that person, so a drop will not reach
          them at all. They have to open the app and turn notifications on themselves.
        </p>
      </div>

      {/* Invited but not signed in yet — the "who's still not in?" list. */}
      {(status.pendingInvites ?? []).length > 0 && (
        <div className="card">
          <span className="tag">Waiting on {status.pendingInvites.length} to sign in</span>
          {status.pendingInvites.map((p: any) => (
            <div className="lb-row" key={p.id}>
              <span className="lb-name muted" style={{ fontSize: 14 }}>{p.email}</span>
              <button
                className="link"
                onClick={() => {
                  if (!window.confirm(`Cancel the invite for ${p.email}?`)) return;
                  op("cancel-invite", { inviteId: p.id });
                }}
              >
                cancel
              </button>
            </div>
          ))}
          <p className="muted">
            They just install the app and sign in with that exact email and they'll join automatically.
            If someone here is already playing, he signed in with a different email. Check his address in
            the list above, then cancel this invite.
          </p>
        </div>
      )}

      {/* A drop with no approved questions is silently skipped, so surface the
          shortfall here BEFORE it costs a drop day. */}
      {totalReady < perDrop && season?.status !== "complete" && season?.status !== "paused" && (
        <div className="card live">
          <span className="tag">⚠️ Not enough questions for the next drop</span>
          <p>
            The league has <b>{totalReady}</b> approved question{totalReady === 1 ? "" : "s"} ready and you asked for
            <b> {perDrop}</b> per drop. A short drop still runs with whatever is available, so this is a
            heads up, not a blocker. Only a drop with <b>zero</b> questions gets skipped.
          </p>
          <p className="muted">Chase the guys below for a check-in.</p>
        </div>
      )}

      {lowInventory.length > 0 && season?.status !== "complete" && (
        <div className="card">
          <span className="tag">Low inventory</span>
          <p className="muted">Total approved across the league: {totalReady}</p>
          {lowInventory.map((m: any) => <p key={m.userId} className="muted">{m.name}: 0 ready, needs an update</p>)}
        </div>
      )}

      {/* Drop operations */}
      <div className="card">
        <span className="tag">Drops</span>
        <p className="muted">Scheduled: {status.dropCounts.scheduled} · Live: {status.dropCounts.live} · Revealed: {status.dropCounts.revealed} · Cancelled: {status.dropCounts.cancelled}</p>
        <p className="muted">Exact future drop times are hidden. You're competing too.</p>
        <p className="muted">
          Opens a drop immediately and notifies everybody. It <b>ignores your drop time range</b>, so it
          works at any hour. Only two things stop it: a drop already being live, or nobody having an
          approved question.
        </p>
        <button
          className="btn ghost"
          onClick={() => {
            if (!window.confirm("Open a drop right now?\n\nEveryone gets notified and it counts for real, exactly like a scheduled drop.")) return;
            op("drop-now");
          }}
        >
          Start a drop now
        </button>
        <div className="row">
          {season?.status === "active" && <button className="btn ghost" onClick={() => op("pause")}>Pause future drops</button>}
          {season?.status === "paused" && <button className="btn ghost" onClick={() => op("resume")}>Resume</button>}
          {season?.status !== "complete" && (
            <button className="btn danger" onClick={() => {
              if (!window.confirm("End the season?\n\nNo more drops will open. You can reopen it afterwards if you end it by mistake.")) return;
              op("end");
            }}>End season</button>
          )}
          {season?.status === "complete" && (
            <button className="btn primary" onClick={() => op("reopen")}>Reopen season</button>
          )}
        </div>
      </div>
    </div>
  );
}
